'use client';

import { Search } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { DemoFrame, useDemoLoop } from './_lib/demo';

/**
 * Domain 1 — Ads research. A grid of competitor ad cards is scanned by a teal
 * sweep; relevance scores appear; the longest-running winner is flagged and its
 * hook is extracted. Conveys: we mine what already works (ads, copy, hooks).
 */
export interface AdsResearchCopy {
  frameLabel: string;
  top: string;
  hookLabel: string;
  hook: string;
}

const WINNER = 4;
const SCORES = [62, 71, 48, 90, 96, 55];

export function AdsResearchAnimation({ copy }: { copy: AdsResearchCopy }) {
  // steps: 0 scan, 1 scores in, 2 winner flagged, 3 hook extracted
  const { ref, step, reduce, fading, inView } = useDemoLoop(4, {
    durations: [1400, 1100, 1300],
    holdMs: 2500,
  });

  return (
    <DemoFrame ref={ref} label={copy.frameLabel} fading={fading}>
      <div className="relative h-full">
        <div className="grid h-full grid-cols-3 grid-rows-2 gap-2.5">
          {SCORES.map((score, i) => {
            const isWinner = i === WINNER && step >= 2;
            return (
              <div
                key={i}
                className={`relative flex flex-col gap-1.5 rounded-lg border p-2 transition-colors duration-300 ${
                  isWinner
                    ? 'border-[var(--sel-accent)] bg-[var(--sel-accent-soft)]/40'
                    : 'border-[var(--sel-line)] bg-[var(--sel-bg)]'
                }`}
              >
                <div className="h-5 rounded bg-[var(--sel-elev)]" />
                <div className="h-1.5 w-3/4 rounded-full bg-[var(--sel-line)]" />
                <div className="h-1.5 w-1/2 rounded-full bg-[var(--sel-line)]" />

                <AnimatePresence>
                  {step >= 1 && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.25, delay: i * 0.05 }}
                      className={`sel-mono absolute top-1 right-1 rounded px-1 text-[0.625rem] font-semibold ${
                        isWinner
                          ? 'bg-[var(--sel-accent)] text-white'
                          : 'bg-[var(--sel-card)] text-[var(--sel-muted)]'
                      }`}
                    >
                      {score}
                    </motion.span>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {isWinner && (
                    <motion.span
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute -top-2 left-1 rounded-full bg-[var(--sel-highlight-bright)] px-1.5 py-0.5 text-[0.5625rem] font-bold text-[var(--sel-highlight-ink)]"
                    >
                      {copy.top}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Teal scan sweep — disabled under reduced motion, paused offscreen.
            Full-height wrapper so translateY percentages resolve against the
            grid height (transform-only, no layout-property animation). */}
        {!reduce && inView && step < 2 && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden"
          >
            <motion.div
              className="absolute inset-0"
              initial={{ y: '-15%' }}
              animate={{ y: '105%' }}
              transition={{
                duration: 1.4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <div className="absolute inset-x-0 top-0 h-10 rounded-full bg-gradient-to-b from-transparent via-[var(--sel-accent)]/25 to-transparent" />
            </motion.div>
          </div>
        )}

        {/* Extracted hook */}
        <AnimatePresence>
          {step >= 3 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="absolute inset-x-0 bottom-0 flex items-center gap-2 rounded-lg border border-[var(--sel-line)] bg-[var(--sel-card)] px-3 py-2 shadow-sm"
            >
              <Search className="h-3.5 w-3.5 shrink-0 text-[var(--sel-accent)]" />
              <span className="truncate text-xs text-[var(--sel-ink)]">
                <span className="text-[var(--sel-muted)]">
                  {copy.hookLabel}{' '}
                </span>
                {copy.hook}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DemoFrame>
  );
}
